import type { DashboardDateRangePreset, DashboardParameter } from "@t3tools/contracts";
import { useId, useState } from "react";

import { cn } from "~/lib/utils";

import {
  DashboardFilterField,
  DashboardFilterRow,
  DashboardSelect,
  FilterToggle,
  dashboardControlClassName,
} from "../dashboardKit";
import { formatArtifactValue } from "./artifactFormat";
import {
  DATE_RANGE_PRESET_LABELS,
  artifactParameterOptions,
  resolveArtifactDateRange,
  validateArtifactParameterValue,
  type ArtifactDateRangeValue,
  type ArtifactOptionsState,
  type ArtifactParameterValue,
  type ArtifactParameterValues,
  type ArtifactSelectOption,
} from "./artifactParameters";

const CUSTOM_RANGE = "custom";

type DateRangeParameter = Extract<DashboardParameter, { kind: "date_range" }>;

type SelectOption = ArtifactSelectOption;

function formatRange(value: ArtifactDateRangeValue, now: Date): string {
  const range = resolveArtifactDateRange(value, now);
  const column = { name: "date", type: "date" } as const;
  return `${formatArtifactValue(range.start, column)} – ${formatArtifactValue(range.end, column)}`;
}

function DateRangeControl(props: {
  now: Date;
  onChange: (value: ArtifactDateRangeValue) => void;
  parameter: DateRangeParameter;
  value: ArtifactDateRangeValue;
}) {
  const id = useId();
  const resolved = resolveArtifactDateRange(props.value, props.now);
  const [draft, setDraft] = useState(resolved);
  const [error, setError] = useState<string | null>(null);
  const isCustom = !("preset" in props.value);
  const valueKey = isCustom ? `${resolved.start}..${resolved.end}` : "preset";
  const [seenValueKey, setSeenValueKey] = useState(valueKey);
  if (seenValueKey !== valueKey) {
    setSeenValueKey(valueKey);
    setDraft(resolved);
    setError(null);
  }

  const commit = (next: { start: string; end: string }) => {
    setDraft(next);
    const valid = validateArtifactParameterValue(props.parameter, next);
    if (!valid) {
      setError(
        next.start > next.end
          ? "Start must be on or before end."
          : `Pick at most ${props.parameter.maxDays} days.`,
      );
      return;
    }
    setError(null);
    props.onChange(valid as ArtifactDateRangeValue);
  };

  return (
    <div className="flex flex-wrap items-end gap-2">
      <DashboardFilterField htmlFor={`${id}-preset`} label={props.parameter.label}>
        <DashboardSelect
          id={`${id}-preset`}
          value={isCustom ? CUSTOM_RANGE : (props.value as { preset: string }).preset}
          onChange={(event) => {
            const choice = event.target.value;
            if (choice === CUSTOM_RANGE) {
              commit(resolved);
              return;
            }
            setError(null);
            props.onChange({ preset: choice as DashboardDateRangePreset });
          }}
        >
          {Object.entries(DATE_RANGE_PRESET_LABELS).map(([preset, label]) => (
            <option key={preset} value={preset}>
              {label}
            </option>
          ))}
          <option value={CUSTOM_RANGE}>Custom range</option>
        </DashboardSelect>
      </DashboardFilterField>
      {isCustom ? (
        <>
          <DashboardFilterField htmlFor={`${id}-start`} label="From">
            <input
              id={`${id}-start`}
              type="date"
              className={cn(dashboardControlClassName, "min-w-0")}
              value={draft.start}
              max={draft.end}
              onChange={(event) => commit({ ...draft, start: event.target.value })}
            />
          </DashboardFilterField>
          <DashboardFilterField htmlFor={`${id}-end`} label="To">
            <input
              id={`${id}-end`}
              type="date"
              className={cn(dashboardControlClassName, "min-w-0")}
              value={draft.end}
              min={draft.start}
              onChange={(event) => commit({ ...draft, end: event.target.value })}
            />
          </DashboardFilterField>
        </>
      ) : null}
      {error ? (
        <p role="alert" className="basis-full text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function MultiSelectControl(props: {
  label: string;
  onChange: (value: string[]) => void;
  options: SelectOption[];
  value: ReadonlyArray<string>;
}) {
  const selected = new Set(props.value);
  return (
    <div role="group" aria-label={props.label} className="grid gap-1">
      <span className="text-xs font-medium text-muted-foreground">{props.label}</span>
      <div className="flex flex-wrap gap-1.5">
        {props.options.map((option) => (
          <FilterToggle
            key={option.value}
            active={selected.has(option.value)}
            onClick={() =>
              props.onChange(
                selected.has(option.value)
                  ? props.value.filter((value) => value !== option.value)
                  : [...props.value, option.value],
              )
            }
          >
            {option.label}
          </FilterToggle>
        ))}
      </div>
    </div>
  );
}

/** Spec parameters as the kit's filter chrome. Emits complete, valid value sets. */
export function ArtifactParameterControls(props: {
  now: Date;
  onChange: (values: ArtifactParameterValues) => void;
  optionsData: ArtifactOptionsState["data"];
  parameters: ReadonlyArray<DashboardParameter>;
  values: ArtifactParameterValues;
}) {
  const id = useId();
  if (!props.parameters.length) return null;
  const update = (parameterId: string, value: ArtifactParameterValue) =>
    props.onChange({ ...props.values, [parameterId]: value });
  const firstRange = props.parameters.find((parameter) => parameter.kind === "date_range");

  return (
    <DashboardFilterRow
      trailing={
        firstRange ? (
          <span data-artifact-range="">
            {formatRange(props.values[firstRange.id] as ArtifactDateRangeValue, props.now)} (ET)
          </span>
        ) : undefined
      }
    >
      {props.parameters.map((parameter) => {
        const value = props.values[parameter.id]!;
        if (parameter.kind === "date_range") {
          return (
            <DateRangeControl
              key={parameter.id}
              now={props.now}
              parameter={parameter}
              value={value as ArtifactDateRangeValue}
              onChange={(next) => update(parameter.id, next)}
            />
          );
        }
        const optionsData =
          parameter.options.from === "dataset"
            ? props.optionsData.get(parameter.options.dataset)
            : undefined;
        const options = artifactParameterOptions(parameter, optionsData);
        if (parameter.kind === "multi_select") {
          return (
            <MultiSelectControl
              key={parameter.id}
              label={parameter.label}
              options={options}
              value={value as ReadonlyArray<string>}
              onChange={(next) => update(parameter.id, next)}
            />
          );
        }
        const current = value as string;
        const withCurrent = options.some((option) => option.value === current)
          ? options
          : [{ value: current, label: current }, ...options];
        return (
          <DashboardFilterField
            key={parameter.id}
            htmlFor={`${id}-${parameter.id}`}
            label={parameter.label}
          >
            <DashboardSelect
              id={`${id}-${parameter.id}`}
              value={current}
              onChange={(event) => update(parameter.id, event.target.value)}
            >
              {withCurrent.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </DashboardSelect>
          </DashboardFilterField>
        );
      })}
    </DashboardFilterRow>
  );
}
